import {Button, Form, Modal} from "react-bootstrap";
import {RouteComponentProps, useParams} from "react-router-dom";
import React from "react";
import {Formik} from "formik";
import {FormikControl, FormRowGroup} from "../Components";
import {DataAPI, MutationAPI, useData, useMutator} from "../../services/API";
import {Validation} from "../../commons/Utils";
import type {UserAdminInfo} from "../../services/API";
import {ModalUrl, useHandleClose} from "../../commons/ModalHelpers";
import UsersTab from "../UsersTab";
import {EmailRegexp} from "../Settings";
import {useQueryClient} from "react-query";
import Notifications from "../Notifications";

/** Zbiór znaków loginu. Ta sama reguła co w serwerowym UserValidator. */
const UsernameChars = /^[A-Za-z0-9._-]+$/;

const AddEditUser = (props: RouteComponentProps) => {

    const { id } = useParams();
    const isNew = id === 'new';
    const queryClient = useQueryClient();

    const handleClose = useHandleClose(UsersTab.url);
    const saveUser = useMutator(MutationAPI.addEditUser, {
        afterSuccess: () => {
            queryClient.invalidateQueries('user-list');
            if (!isNew) queryClient.invalidateQueries(['managed-user', id]);
            handleClose();
        },
        customMsg: isNew
            ? 'Użytkownik został utworzony, link do ustawienia hasła wysłano na podany adres'
            : 'Użytkownik został zaktualizowany. Zmiana uprawnień zadziała po jego ponownym zalogowaniu'
    });
    const { isLoading, error, data } = useData(id, 'managed-user', DataAPI.getManagedUser, {enabled: !isNew});

    const submit = (values: UserAdminInfo) => {
        if (!isNew) values.id = user.id;
        saveUser.mutate(values);
    }

    const validate = (values: UserAdminInfo) => {
        let err = {};
        if (isNew) {
            //Validation.length przepuszcza pustą wartość, dlatego najpierw present
            Validation.add(err, 'username', Validation.present(values.username, 'Login jest wymagany'));
            Validation.add(err, 'username', Validation.length(values.username, 3, 'Login musi mieć co najmniej 3 znaki'));
            //Validation.add skleja komunikaty bez odstępu, dlatego dla loginu pokazujemy tylko pierwszy powód
            if (values.username) {
                if (values.username.indexOf('@') > -1)
                    Validation.add(err, 'username', 'Login nie może zawierać znaku @ - domena organizacji jest doklejana automatycznie');
                else if (values.username.indexOf(' ') > -1)
                    Validation.add(err, 'username', 'Login nie może zawierać spacji');
                else if (!UsernameChars.test(values.username))
                    Validation.add(err, 'username', 'Login może zawierać tylko litery, cyfry, kropkę, myślnik i podkreślenie');
            }
        }
        Validation.add(err, 'firstName', Validation.present(values.firstName, 'Imię jest wymagane'));
        Validation.add(err, 'lastName', Validation.present(values.lastName, 'Nazwisko jest wymagane'));
        Validation.add(err, 'email', Validation.present(values.email, 'Adres e-mail jest wymagany, na niego wysyłane jest hasło'));
        if (values.email) Validation.add(err, 'email', Validation.regexp(values.email, EmailRegexp, 'Nieprawidłowy adres e-mail'));
        return err;
    }

    if (isLoading) return null;
    if (error) {
        Notifications.pushNew(error.message, 'danger');
        return null;
    }

    const user: UserAdminInfo = data;

    const initial = isNew
        ? {username: '', firstName: '', lastName: '', email: '', admin: false}
        : {username: user.username, firstName: user.firstName, lastName: user.lastName,
           email: user.email, admin: user.admin};

    return <Modal size="md" show={true} onHide={handleClose} animation={false}>
        <Formik initialValues={initial} onSubmit={submit} validate={validate}>
            {({ handleSubmit }) => (
                <Form noValidate onSubmit={handleSubmit} className="form form-add-user">
                    <Modal.Header closeButton>
                        <Modal.Title>{isNew ? "Utwórz użytkownika" : (user.username + ' - Edycja')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FormRowGroup label="Login" controlId="usernameId">
                            {/* login jest częścią adresu logowania i został już wysłany e-mailem, dlatego nie da się go zmienić */}
                            <FormikControl autoFocus={isNew} type={isNew ? "text" : "display"} name="username" />
                        </FormRowGroup>
                        <FormRowGroup label="Imię" controlId="firstNameId">
                            <FormikControl autoFocus={!isNew} type="text" name="firstName" />
                        </FormRowGroup>
                        <FormRowGroup label="Nazwisko" controlId="lastNameId">
                            <FormikControl type="text" name="lastName" />
                        </FormRowGroup>
                        <FormRowGroup label="E-mail" controlId="emailId">
                            <FormikControl type="text" name="email" />
                        </FormRowGroup>
                        <FormRowGroup label="" controlId="adminId">
                            <FormikControl type="checkbox" name="admin" label="Administrator" />
                        </FormRowGroup>
                        {isNew ?
                            <p className="text-muted">Na podany adres zostanie wysłany link, pod którym użytkownik sam ustawi hasło.</p>
                            : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleClose}>Anuluj</Button>
                        <Button variant="primary" onClick={handleSubmit}>{isNew ? "Utwórz" : "Aktualizuj"}</Button>
                    </Modal.Footer>
                </Form>
            )}
        </Formik>
    </Modal>
}

export default AddEditUser;

AddEditUser.url = `/users${ModalUrl}edit/:id`
AddEditUser.buildUrl = user => AddEditUser.url.replace(':id', user ? user : 'new')
